import type { Configuration } from '@azure/msal-browser';

import { environment } from './environment';
import { TenantConfig } from './tenant-config';

/**
 * WI-F2 — Pure MSAL config builders (ADO #38530).
 *
 * These replace the old eager `apps/web-client/src/authConfig.ts`, which built
 * module-level `msalConfig` / `msalScopes` / `presenceScope` from build-time
 * env at import time AND was imported UP into `libs/core` (the architectural
 * inversion). The builders now live HERE in `libs/core` and are PURE: they take
 * a runtime `TenantConfig` and RETURN the MSAL config + scopes. Config flows
 * app → libs by injection; `libs/core` no longer imports from `apps/web-client`.
 *
 * Notably absent: any client `secret`. `msal-browser` is a PUBLIC client and
 * ignores a secret; shipping one to the browser was a security smell. WI-F8
 * removes it from env/.env entirely — it is deliberately NOT reconstructed here.
 * Also dropped: the dead `graphApiScope` export (no consumers).
 */

/** MSAL scopes injected into the instance manager and login/token requests. */
export interface MsalScopes {
  /**
   * API scopes for login + silent token acquisition. Server-provided by
   * `/config` (e.g. `["<clientId>/.default"]`) — used verbatim as an array.
   */
  api: string[];
  /**
   * Graph presence scope. Dormant (no caller passes `isGraphRequest=true`) and
   * tenant-independent, so it stays a build-time value — injected, not wired.
   */
  presence: string;
}

/**
 * Build the MSAL `Configuration` from a runtime {@link TenantConfig}.
 *
 * No `secret` (public client). `redirectUri` / `postLogoutRedirectUri` both use
 * the tenant's `redirectUri` (`https://<domain>`), matching prior behavior which
 * used a single redirect base for both.
 */
export const buildMsalConfig = (tenant: TenantConfig): Configuration => ({
  auth: {
    clientId: tenant.clientId,
    authority: tenant.authority,
    redirectUri: tenant.redirectUri,
    postLogoutRedirectUri: tenant.redirectUri,
    clientCapabilities: ['CP1'],
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
});

/**
 * Build the injectable {@link MsalScopes} from a runtime {@link TenantConfig}.
 *
 * `api` comes straight from the server-provided `tenant.scopes`; `presence`
 * comes from the build-time env (dormant Graph scope).
 */
export const buildMsalScopes = (tenant: TenantConfig): MsalScopes => ({
  api: tenant.scopes,
  presence: (environment.NX_PRESENCE_SCOPE as string) ?? '',
});
